import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from './utils';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  description?: ReactNode;
}

export function Card({ className, title, description, children, ...props }: CardProps) {
  return (
    <section className={cx('ui-card', className)} {...props}>
      {title || description ? (
        <header className="ui-card__header">
          {title ? <h3 className="ui-card__title">{title}</h3> : null}
          {description ? <p className="ui-card__description">{description}</p> : null}
        </header>
      ) : null}
      <div className="ui-card__body">{children}</div>
    </section>
  );
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: 'neutral' | 'accent' | 'success' | 'warning' | 'danger';
}

export function Badge({ className, tone = 'neutral', ...props }: BadgeProps) {
  return <span className={cx('ui-badge', `ui-badge--${tone}`, className)} {...props} />;
}

export const Tag = Badge;

export interface AlertProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: ReactNode;
}

export function Alert({ className, tone = 'info', title, children, ...props }: AlertProps) {
  return (
    <div className={cx('ui-alert', `ui-alert--${tone}`, className)} role="status" {...props}>
      {title ? <strong className="ui-alert__title">{title}</strong> : null}
      <div className="ui-alert__body">{children}</div>
    </div>
  );
}

export interface EmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="ui-empty-state">
      <h3 className="ui-empty-state__title">{title}</h3>
      {description ? <p className="ui-empty-state__description">{description}</p> : null}
      {action ? <div className="ui-empty-state__action">{action}</div> : null}
    </div>
  );
}
